import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, 
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip
} from 'recharts';
import {
  Shield, AlertTriangle, CheckCircle2, RefreshCw, Target, Loader2, 
  AlertCircle, ChevronRight, FileText, TrendingDown, Award, ClipboardCheck,
  Play, Eye, Gauge, Zap, Calendar, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CHAPReadinessPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [simulating, setSimulating] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/chap-readiness/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboardData(data);
      }
    } catch (error) {
      console.error('Error fetching dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const res = await fetch(`${API_URL}/api/chap-readiness/simulate-survey`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Survey simulation complete');
        fetchDashboard();
      }
    } catch (error) {
      toast.error('Simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-[#00b0ab]" />
      </div>
    );
  }

  const { 
    audit_risk_index, 
    readiness_score, 
    category_scores, 
    drift_alerts, 
    survey_simulation,
    common_deficiencies 
  } = dashboardData || {};

  // Prepare radar data
  const radarData = category_scores ? Object.entries(category_scores).map(([key, cat]) => ({
    category: cat.name.split(' ')[0],
    score: cat.score,
    fullMark: 100
  })) : [];

  return (
    <div className="space-y-6" data-testid="chap-readiness-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Shield className="h-7 w-7 text-[#00b0ab]" />
            CHAP Readiness & Audit Risk
          </h1>
          <p className="text-slate-500 mt-1">Survey readiness score, compliance drift detection & simulation</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchDashboard}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button onClick={runSimulation} disabled={simulating} className="bg-indigo-600 hover:bg-indigo-700">
            {simulating ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Run Survey Simulation
          </Button>
        </div>
      </div>

      {/* Main Score Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Readiness Score */}
        <Card className={`border-2 ${
          readiness_score >= 80 ? 'border-emerald-300 bg-emerald-50' :
          readiness_score >= 60 ? 'border-amber-300 bg-amber-50' :
          'border-red-300 bg-red-50'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">CHAP Readiness Score</p>
                <p className="text-4xl font-bold">{readiness_score || 0}</p>
                <Badge className={
                  readiness_score >= 80 ? 'bg-emerald-100 text-emerald-700' :
                  readiness_score >= 60 ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }>
                  {readiness_score >= 80 ? 'Survey Ready' : readiness_score >= 60 ? 'Needs Work' : 'At Risk'}
                </Badge>
              </div>
              <Gauge className={`h-12 w-12 ${
                readiness_score >= 80 ? 'text-emerald-500' :
                readiness_score >= 60 ? 'text-amber-500' : 'text-red-500'
              }`} />
            </div>
          </CardContent>
        </Card>

        {/* Audit Risk Index */}
        <Card className={`border-2 ${
          audit_risk_index?.score <= 20 ? 'border-emerald-300 bg-emerald-50' :
          audit_risk_index?.score <= 40 ? 'border-amber-300 bg-amber-50' :
          'border-red-300 bg-red-50'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Audit Risk Index</p>
                <p className="text-4xl font-bold">{audit_risk_index?.score || 0}</p>
                <Badge className={
                  audit_risk_index?.color === 'green' ? 'bg-emerald-100 text-emerald-700' :
                  audit_risk_index?.color === 'yellow' ? 'bg-amber-100 text-amber-700' :
                  'bg-red-100 text-red-700'
                }>
                  {audit_risk_index?.risk_level || 'Unknown'}
                </Badge>
              </div>
              <AlertTriangle className={`h-12 w-12 ${
                audit_risk_index?.color === 'green' ? 'text-emerald-500' :
                audit_risk_index?.color === 'yellow' ? 'text-amber-500' : 'text-red-500'
              }`} />
            </div>
          </CardContent>
        </Card>

        {/* Simulation Result */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Simulated Survey Score</p>
                <p className="text-4xl font-bold">{survey_simulation?.simulated_score || 0}</p>
                <Badge className={survey_simulation?.pass_likely ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}>
                  {survey_simulation?.pass_likely ? 'Pass Likely' : 'At Risk'}
                </Badge>
              </div>
              <ClipboardCheck className={`h-12 w-12 ${survey_simulation?.pass_likely ? 'text-emerald-500' : 'text-red-500'}`} />
            </div>
          </CardContent>
        </Card>

        {/* Drift Alerts */}
        <Card className={drift_alerts?.length > 0 ? 'border-amber-200 bg-amber-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600">Compliance Drift</p>
                <p className="text-4xl font-bold">{drift_alerts?.length || 0}</p>
                <Badge className={drift_alerts?.length > 0 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}>
                  {drift_alerts?.length > 0 ? 'Alerts Active' : 'Stable'}
                </Badge>
              </div>
              <TrendingDown className={`h-12 w-12 ${drift_alerts?.length > 0 ? 'text-amber-500' : 'text-emerald-500'}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="overview"><Shield className="h-4 w-4 mr-1" /> Overview</TabsTrigger>
          <TabsTrigger value="categories"><ClipboardCheck className="h-4 w-4 mr-1" /> Categories</TabsTrigger>
          <TabsTrigger value="risks"><AlertTriangle className="h-4 w-4 mr-1" /> Risk Factors</TabsTrigger>
          <TabsTrigger value="simulation"><Play className="h-4 w-4 mr-1" /> Simulation</TabsTrigger>
          <TabsTrigger value="drift"><TrendingDown className="h-4 w-4 mr-1" /> Drift</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Radar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Compliance Category Scores</CardTitle>
                <CardDescription>Performance across CHAP compliance categories</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-80">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid />
                      <PolarAngleAxis dataKey="category" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis angle={30} domain={[0, 100]} />
                      <Radar name="Score" dataKey="score" stroke="#6366f1" fill="#a5b4fc" fillOpacity={0.6} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle>Survey Readiness Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {audit_risk_index?.risk_factors?.length > 0 ? (
                  audit_risk_index.risk_factors.slice(0, 5).map((factor, idx) => (
                    <div key={idx} className="p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <AlertCircle className="h-4 w-4 text-red-600" />
                          <span className="text-sm">{factor.factor}</span>
                        </div>
                        <Badge className="bg-red-100 text-red-700">Impact: {factor.impact}</Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                    <p className="text-emerald-600 font-medium">No Critical Risk Factors</p>
                    <p className="text-sm text-slate-400">Continue maintaining compliance standards</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Categories Tab */}
        <TabsContent value="categories">
          <Card>
            <CardHeader>
              <CardTitle>CHAP Compliance Categories</CardTitle>
              <CardDescription>Detailed breakdown by compliance area</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {category_scores && Object.entries(category_scores).map(([key, cat]) => (
                  <div key={key} className="p-4 border rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <h4 className="font-medium">{cat.name}</h4>
                        <p className="text-xs text-slate-400">Weight: {(cat.weight * 100)}%</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className={`text-2xl font-bold ${
                          cat.score >= 90 ? 'text-emerald-600' : 
                          cat.score >= 80 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {cat.score}%
                        </span>
                        <Badge className={
                          cat.score >= 90 ? 'bg-emerald-100 text-emerald-700' : 
                          cat.score >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }>
                          {cat.score >= 90 ? 'Compliant' : cat.score >= 80 ? 'Monitor' : 'Action Needed'}
                        </Badge>
                      </div>
                    </div>
                    <Progress value={cat.score} className="h-2 mb-2" />
                    <div className="flex flex-wrap gap-1 mt-2">
                      {cat.standards?.slice(0, 3).map((std, idx) => (
                        <span key={idx} className="text-xs bg-slate-100 px-2 py-1 rounded">{std}</span>
                      ))}
                      {cat.standards?.length > 3 && (
                        <span className="text-xs text-slate-400">+{cat.standards.length - 3} more</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Risk Factors Tab */}
        <TabsContent value="risks">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Active Risk Factors
                </CardTitle>
              </CardHeader>
              <CardContent>
                {audit_risk_index?.risk_factors?.length > 0 ? (
                  <div className="space-y-3">
                    {audit_risk_index.risk_factors.map((factor, idx) => (
                      <div key={idx} className={`p-3 rounded-lg border ${
                        factor.impact >= 10 ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                      }`}>
                        <div className="flex items-start gap-2">
                          <AlertCircle className={`h-5 w-5 mt-0.5 ${factor.impact >= 10 ? 'text-red-600' : 'text-amber-600'}`} />
                          <div className="flex-1">
                            <p className="font-medium">{factor.factor}</p>
                            <p className="text-sm text-slate-600">Risk Impact: {factor.impact} points</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-emerald-500" />
                    <p className="font-medium text-emerald-600">No Active Risk Factors</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Common CHAP Deficiencies</CardTitle>
                <CardDescription>Frequently cited survey findings</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {common_deficiencies?.map((def, idx) => (
                    <div key={idx} className="p-2 bg-slate-50 rounded flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">{def.area}</p>
                        <p className="text-xs text-slate-500">{def.description}</p>
                      </div>
                      <Badge variant="outline">Risk: {def.risk_weight}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Simulation Tab */}
        <TabsContent value="simulation">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Play className="h-5 w-5 text-[#00b0ab]" />
                Survey Simulation Results
              </CardTitle>
              <CardDescription>Mock CHAP survey based on current compliance data</CardDescription>
            </CardHeader>
            <CardContent>
              {survey_simulation ? (
                <div className="space-y-6">
                  {/* Score Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className={`p-4 rounded-lg text-center ${
                      survey_simulation.pass_likely ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                    }`}>
                      <p className="text-4xl font-bold">{survey_simulation.simulated_score}</p>
                      <p className="text-sm text-slate-600">Simulated Score</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center border border-red-200">
                      <p className="text-4xl font-bold text-red-600">{survey_simulation.findings_count?.conditions || 0}</p>
                      <p className="text-sm text-slate-600">Conditions</p>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center border border-amber-200">
                      <p className="text-4xl font-bold text-amber-600">{survey_simulation.findings_count?.observations || 0}</p>
                      <p className="text-sm text-slate-600">Observations</p>
                    </div>
                  </div>

                  {/* Findings */}
                  {survey_simulation.findings?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">Simulated Findings</h4>
                      <div className="space-y-2">
                        {survey_simulation.findings.map((finding, idx) => (
                          <div key={idx} className={`p-3 rounded-lg border ${
                            finding.severity === 'Condition' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                          }`}>
                            <div className="flex items-center gap-2 mb-1">
                              <Badge className={finding.severity === 'Condition' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                                {finding.severity}
                              </Badge>
                              <span className="font-medium">{finding.area}</span>
                            </div>
                            <p className="text-sm text-slate-600">{finding.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommendations */}
                  {survey_simulation.recommendations?.length > 0 && (
                    <div>
                      <h4 className="font-semibold mb-3">Recommendations</h4>
                      <div className="space-y-2">
                        {survey_simulation.recommendations.map((rec, idx) => (
                          <div key={idx} className="flex items-start gap-2 p-3 bg-[#00b0ab]/10 rounded-lg">
                            <ChevronRight className="h-5 w-5 text-[#00b0ab] mt-0.5" />
                            <p className="text-sm text-indigo-800">{rec}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Play className="h-16 w-16 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No simulation data available</p>
                  <p className="text-sm text-slate-400">Click "Run Survey Simulation" to generate results</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Drift Tab */}
        <TabsContent value="drift">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-amber-500" />
                Compliance Drift Detection
              </CardTitle>
              <CardDescription>Metrics trending in the wrong direction</CardDescription>
            </CardHeader>
            <CardContent>
              {drift_alerts?.length > 0 ? (
                <div className="space-y-3">
                  {drift_alerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 rounded-lg border ${
                      alert.severity === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <TrendingDown className={`h-5 w-5 ${alert.severity === 'high' ? 'text-red-600' : 'text-amber-600'}`} />
                          <div>
                            <p className="font-medium">{alert.metric}</p>
                            <p className="text-sm text-slate-600">
                              {alert.current}% (was {alert.previous}%)
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge className={alert.severity === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                            {alert.change > 0 ? '+' : ''}{alert.change}%
                          </Badge>
                          <p className="text-xs text-slate-400 mt-1">{alert.direction}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle2 className="h-16 w-16 mx-auto mb-4 text-emerald-500" />
                  <p className="font-medium text-emerald-600">No Compliance Drift Detected</p>
                  <p className="text-sm text-slate-400">All metrics are stable or improving</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
